import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Article } from "@/data/articles";
import { Language } from "@/lib/translations";

interface ArticleCardProps {
  article: Article;
  language: Language;
  href: string;
}

export function ArticleCard({ article, language, href }: ArticleCardProps) {
  const meta = article[language];

  return (
    <article className="group flex flex-col md:flex-row gap-0 rounded-2xl border border-border/40 bg-card overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
      {/* Image */}
      <div className="md:w-2/5 flex-shrink-0 overflow-hidden">
        <img
          src={article.image}
          alt={meta.title}
          className="w-full h-52 md:h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>

      {/* Content */}
      <div className="flex flex-col justify-between p-6 md:p-8 flex-1">
        <div>
          <span className="inline-block text-xs font-bold uppercase tracking-[0.15em] text-primary mb-4">
            {meta.category}
          </span>

          <h2 className="font-['Source_Serif_4'] text-xl md:text-2xl leading-[1.25] tracking-tight text-foreground font-semibold mb-3 group-hover:text-primary transition-colors duration-200">
            {meta.title}
          </h2>

          <p className="text-sm md:text-base leading-relaxed text-muted-foreground line-clamp-3">
            {meta.subtitle}
          </p>
        </div>

        <div className="mt-6 flex items-end justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15 text-primary font-bold text-sm font-['Funnel_Display'] flex-shrink-0">
              R
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-foreground leading-tight">
                {language === "fr" ? "Équipe ReFound" : "ReFound Team"}
              </span>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <time>{meta.date}</time>
                <span>·</span>
                <span>{meta.readingTime}</span>
              </div>
            </div>
          </div>

          <Link
            to={href}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all duration-200 flex-shrink-0"
          >
            {language === "fr" ? "Lire l'article" : "Read article"}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </article>
  );
}
